import type { ArcadeControlCode } from '@/lib/arcade-controls';

// Only browser-normalized mappings are understood here. An empty mapping needs
// a verified device profile/calibration rather than guessed button/axis indices.
export type GamepadSnapshot = Pick<Gamepad, 'connected' | 'mapping' | 'buttons' | 'axes'>;

export function readGamepads(
  source: { getGamepads?: () => ArrayLike<Gamepad | null> },
): Array<Gamepad | null> {
  try {
    return typeof source.getGamepads === 'function'
      ? Array.from(source.getGamepads())
      : [];
  } catch {
    // Disabled policy, insecure context, or unavailable platform support must
    // release previous input, without terminating the animation loop.
    return [];
  }
}

export function firstConnectedGamepads<T extends { connected: boolean }>(
  gamepads: ArrayLike<T | null>,
): T[] {
  return Array.from(gamepads)
    .filter((gamepad): gamepad is T => !!gamepad?.connected)
    .slice(0, 2);
}

export function standardGamepadToArcadeControls(
  gamepad: GamepadSnapshot,
  player: 1 | 2,
): Partial<Record<ArcadeControlCode, boolean>> {
  if (!gamepad.connected || gamepad.mapping !== 'standard') {
    return {};
  }
  const button = (index: number) => gamepad.buttons[index]?.pressed ?? false;
  const x = gamepad.axes[0] ?? 0;
  const y = gamepad.axes[1] ?? 0;
  const p = `P${player}`;
  return {
    [`${p}_U`]: button(12) || y < -0.5,
    [`${p}_D`]: button(13) || y > 0.5,
    [`${p}_L`]: button(14) || x < -0.5,
    [`${p}_R`]: button(15) || x > 0.5,
    [`${p}_1`]: button(0),
    [`${p}_2`]: button(1),
    [`${p}_3`]: button(2),
    [`${p}_4`]: button(3),
    [`${p}_5`]: button(4),
    [`${p}_6`]: button(5),
    [`START${player}`]: button(9),
  };
}
